import { useState } from "react";
import { toast } from "sonner";
import { Upload, FileSpreadsheet, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

export default function LegerImporterModal({ open, onOpenChange, onSuccess }) {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [errorDetail, setErrorDetail] = useState(null);

  const reset = () => { setFile(null); setLoading(false); setResult(null); setErrorDetail(null); };

  const submit = async () => {
    if (!file) return;
    setLoading(true);
    setResult(null);
    setErrorDetail(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/admin/leger/import", form, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success(`Berhasil impor ${data.rows_processed} baris untuk ${data.students_touched} siswa`);
      onSuccess?.();
    } catch (err) {
      const d = err.response?.data?.detail;
      if (typeof d === "object" && d?.missing_nisn) {
        setErrorDetail(d);
        toast.error("Impor Leger ditolak — ada siswa yang belum lengkap");
      } else {
        toast.error(typeof d === "string" ? d : "Gagal impor Leger");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="leger-importer-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-blue-700" /> Impor Leger Excel
          </DialogTitle>
          <DialogDescription>
            Unggah berkas Excel (.xlsx) berisi Nilai per Mata Pelajaran untuk 6 semester. Sistem akan memetakan data ke siswa berdasarkan NISN.
          </DialogDescription>
        </DialogHeader>

        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-900 flex gap-2" data-testid="leger-prerequisite-warning">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div>
            <strong>Prasyarat:</strong> Semua siswa yang ada di berkas Leger harus <strong>sudah terdaftar</strong> di sistem <em>dan</em> biodatanya sudah <strong>Terverifikasi</strong> oleh Admin TU. Impor akan otomatis dibatalkan jika ada siswa yang belum lengkap.
          </div>
        </div>

        <div className="space-y-4">
          <label className="block border-2 border-dashed border-slate-300 rounded-xl p-8 text-center cursor-pointer hover:bg-slate-50 hover:border-blue-400 transition-colors" data-testid="leger-drop-zone">
            <Upload className="w-8 h-8 text-slate-400 mx-auto mb-3" />
            <div className="text-sm font-medium text-slate-900">
              {file ? file.name : "Klik untuk memilih berkas Excel"}
            </div>
            <div className="text-xs text-slate-500 mt-1">
              {file ? `${(file.size / 1024).toFixed(1)} KB` : "Format: .xlsx, .xls (maks 10MB)"}
            </div>
            <input
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); setErrorDetail(null); }}
              data-testid="leger-file-input"
            />
          </label>

          {result && (
            <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg text-sm" data-testid="leger-result">
              <div className="flex items-center gap-2 text-emerald-800 font-semibold mb-1">
                <CheckCircle2 className="w-4 h-4" /> Impor Berhasil
              </div>
              <div className="text-emerald-700 space-y-0.5 text-xs">
                <div>Baris diproses: <strong>{result.rows_processed}</strong></div>
                <div>Siswa terpengaruh: <strong>{result.students_touched}</strong></div>
              </div>
            </div>
          )}

          {errorDetail && (
            <div className="p-4 bg-rose-50 border border-rose-200 rounded-lg text-sm space-y-3" data-testid="leger-error-detail">
              <div className="flex items-center gap-2 text-rose-800 font-semibold">
                <AlertTriangle className="w-4 h-4" /> Impor Dibatalkan
              </div>
              <div className="text-rose-700 text-xs">{errorDetail.message}</div>

              {errorDetail.missing_nisn?.length > 0 && (
                <div>
                  <div className="text-xs font-semibold text-rose-800 mb-1">
                    NISN belum terdaftar ({errorDetail.missing_nisn.length}):
                  </div>
                  <div className="max-h-28 overflow-y-auto text-xs font-mono text-rose-700 bg-white/60 rounded p-2 border border-rose-100">
                    {errorDetail.missing_nisn.join(", ")}
                  </div>
                </div>
              )}

              {errorDetail.not_verified?.length > 0 && (
                <div>
                  <div className="text-xs font-semibold text-rose-800 mb-1">
                    Belum diverifikasi ({errorDetail.not_verified.length}):
                  </div>
                  <div className="max-h-32 overflow-y-auto text-xs bg-white/60 rounded p-2 border border-rose-100 space-y-1">
                    {errorDetail.not_verified.map((s, i) => (
                      <div key={i} className="flex justify-between gap-2">
                        <span className="text-rose-900">{s.nama || <em>Belum ada nama</em>}</span>
                        <span className="font-mono text-rose-600">{s.nisn}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="text-xs text-slate-500 leading-relaxed p-3 bg-slate-50 rounded-lg">
            <strong className="text-slate-700">Format kolom yang didukung:</strong><br />
            NISN, Nama, Kelas, Semester, Mata Pelajaran, KKM, Nilai, Predikat, Deskripsi, Sakit, Izin, Alpa, Ekstrakurikuler, Prestasi_Ekstra
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading} data-testid="leger-cancel-button">Tutup</Button>
            <Button onClick={submit} disabled={!file || loading} className="bg-blue-700 hover:bg-blue-800" data-testid="leger-submit-button">
              {loading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Upload className="w-4 h-4 mr-1.5" />}
              Impor Sekarang
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
